import {
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    TemplateRef,
    ViewChild,
    ViewEncapsulation
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { UserService } from '@lsq-frontend/angular-shared/features/user-service';
import { BankAccount } from '@lsq-frontend/remote-services/enterprise/common';
import {
    AddReserveReleaseRequest,
    AddReserveReleaseService,
    CancelReserveReleaseRequest,
    CancelReserveReleaseService,
    GetAvailabilityByClientRequest,
    GetAvailabilityByClientService,
    GetBankAccountsByClientRequest,
    GetBankAccountsByClientService
} from '@lsq-frontend/remote-services/enterprise/dashboard-services';
import {
    ClassNames,
    Color,
    forLifeOf,
    ISelectOption,
    LifecycleAware,
    Modal,
    Size,
    Utilities
} from '@lsq-frontend/common-ui';
import { Icons } from '@lsq-frontend/common-ui';
import { LsqValidators } from '@lsq-frontend/common-ui';
import { ModalService } from '@lsq-frontend/common-ui';
import { PopoverPlacement } from 'ngx-smart-popover';
import { Subject } from 'rxjs';
import { FormField } from '../../../constants/form-field.constants';

@LifecycleAware()
@Component({
    selector: 'request-funds-card',
    templateUrl: './request-funds-card.component.html',
    styleUrls: ['./request-funds-card.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class RequestFundsCardComponent implements OnInit, OnDestroy {
    @ViewChild('cancelRequestErrorModalTemplate', { read: TemplateRef, static: true })
    public cancelRequestErrorModalTemplate: TemplateRef<any>;
    @ViewChild('requestTemplate', { static: true }) private requestTemplate: TemplateRef<any>;
    @ViewChild('processingTemplate', { static: true }) private processingTemplate: TemplateRef<any>;
    @ViewChild('canceledTemplate', { static: true }) private canceledTemplate: TemplateRef<any>;
    @Input() public isMobile: boolean;
    @Output() public update = new EventEmitter();
    @Output() public loaded = new EventEmitter();
    public addReserveReleaseRequestStream = new Subject<AddReserveReleaseRequest>();
    public cancelReserveReleaseRequest: CancelReserveReleaseRequest;
    public getAvailabilityByClientRequest: GetAvailabilityByClientRequest;
    public getAvailabilityByClientRequestStream = new Subject<GetAvailabilityByClientRequest>();
    public getBankAccountsByClientRequest: GetBankAccountsByClientRequest;
    public getBankAccountsByClientRequestStream = new Subject<GetBankAccountsByClientRequest>();
    public cancelReserveReleaseRequestStream = new Subject<CancelReserveReleaseRequest>();
    public template: TemplateRef<any>;

    private _requestedAmount: number;
    public bankAccountOptions: ISelectOption[] = [];
    public bankAccountsList: BankAccount[] = [];
    public disabledDefaultAmount: boolean;
    public classNames = ClassNames;
    public color = Color;
    public displayNoteInput = false;
    public icon = Icons;
    public initializing = true;
    public placement = PopoverPlacement;
    public size = Size;
    public submitting = false;
    public availability = 0;
    public totalPendingAmount = 0;
    private _savedRequestAmount: number;
    public formGroup = new FormGroup({
        isDefault: new FormControl(true, [Validators.required]),
        notes: new FormControl(''),
        bankAccountKey: new FormControl(null, [
            Validators.required,
            LsqValidators.disallowZero,
            LsqValidators.currency
        ]),
        requestAmount: new FormControl(null, [Validators.required])
    });

    constructor(
        private _addReserveReleaseService: AddReserveReleaseService,
        private _cancelReserveReleaseService: CancelReserveReleaseService,
        private _getAvailabilityByClientService: GetAvailabilityByClientService,
        private _getBankAccountsByClientService: GetBankAccountsByClientService,
        private _modalService: ModalService,
        private _userService: UserService
    ) {
        this.resetFormGroup();
        this.getAvailabilityByClientRequest = new GetAvailabilityByClientRequest();
        this.getBankAccountsByClientRequest = new GetBankAccountsByClientRequest();

        this._getBankAccountsByClientService.setRequestStreamAndAutoExecute(
            this.getBankAccountsByClientRequestStream
        );
        this._getAvailabilityByClientService.setRequestStreamAndAutoExecute(
            this.getAvailabilityByClientRequestStream
        );
        this._addReserveReleaseService.setRequestStreamAndAutoExecute(
            this.addReserveReleaseRequestStream
        );
        this._cancelReserveReleaseService.setRequestStreamAndAutoExecute(
            this.cancelReserveReleaseRequestStream
        );

        this._getBankAccountsByClientService.dataStream
            .pipe(forLifeOf(this))
            .subscribe((response) => {
                this.bankAccountsList = response.rows;
                if (this.bankAccountsList.length > 0) {
                    this.bankAccountOptions = this.bankAccountsList.map((account) => ({
                        value: account.bankAccountKey,
                        label: account.accountName,
                        subLabel: account.payableTo
                    }));
                    this.selectDefaultBankAccount();
                    this.initializing = false;
                }
            });

        this._addReserveReleaseService.dataStream.pipe(forLifeOf(this)).subscribe((response) => {
            this.cancelReserveReleaseRequest = new CancelReserveReleaseRequest({
                reserveReleaseKey: response.reserveReleaseKey
            });
            this.template = this.processingTemplate;
            this.submitting = false;
            this.update.emit();
            this.getAvailabilityByClient(this.getAvailabilityByClientRequest);
        });

        this._getAvailabilityByClientService.dataStream
            .pipe(forLifeOf(this))
            .subscribe((getAvailabilityByClientResponse) => {
                this.availability = getAvailabilityByClientResponse.availableFunds;
                this.totalPendingAmount =
                    getAvailabilityByClientResponse.totalPendingAmount || this.totalPendingAmount;
                this.disabledDefaultAmount = this.availableToWithdraw <= 0;
                this.formGroup.get(FormField.IsDefault).setValue(!this.disabledDefaultAmount);
                if (this.disabledDefaultAmount) {
                    this.setRequestAmount(this.formGroup.get(FormField.IsDefault).value);
                }

                // Let the data render.
                Utilities.executeAfterRender(() => {
                    this.loaded.emit();
                });
            });

        this._cancelReserveReleaseService.dataStream.pipe(forLifeOf(this)).subscribe((response) => {
            this.update.emit();
            if (response.isInProgress) {
                this._modalService.showModal(
                    new Modal({
                        id: 'cancel-reserve-release',
                        title: 'Sorry',
                        templateRef: this.cancelRequestErrorModalTemplate,
                        showFooter: true,
                        showCloseButton: true,
                        showOkButton: true,
                        okButtonText: 'Ok',
                        clickOutsideToClose: false,
                        onOkClick: () => {
                            this._modalService.closeModal();
                        }
                    })
                );
            } else {
                this.template = this.canceledTemplate;
            }
            this.getAvailabilityByClient(this.getAvailabilityByClientRequest);
        });

        this.formGroup
            .get(FormField.IsDefault)
            .valueChanges.pipe(forLifeOf(this))
            .subscribe((val) => {
                if (!this.disabledDefaultAmount) {
                    this.setRequestAmount(val);
                }
            });

        this.getAvailabilityByClient(this.getAvailabilityByClientRequest);
        this.getBankAccountsByClient(this.getBankAccountsByClientRequest);
    }

    public ngOnInit(): void {
        this.template = this.requestTemplate;
    }

    public ngOnDestroy(): void {}

    // Getters.
    public get availableToWithdraw(): number {
        return this.availability - this.totalPendingAmount;
    }

    public get requestedAmount(): number {
        return this._requestedAmount;
    }

    public cancelReserveRelease(request?: CancelReserveReleaseRequest): void {
        this.cancelReserveReleaseRequest = request ? request : this.cancelReserveReleaseRequest;
        this.cancelReserveReleaseRequestStream.next(this.cancelReserveReleaseRequest);
    }

    public getBankAccountsByClient(request?: GetBankAccountsByClientRequest): void {
        this.getBankAccountsByClientRequest = request
            ? request
            : this.getBankAccountsByClientRequest;
        this.getBankAccountsByClientRequestStream.next(this.getBankAccountsByClientRequest);
    }

    public getAvailabilityByClient(request?: GetAvailabilityByClientRequest): void {
        this.getAvailabilityByClientRequest = request
            ? request
            : this.getAvailabilityByClientRequest;
        this.getAvailabilityByClientRequestStream.next(this.getAvailabilityByClientRequest);
    }

    public getRequestedBankAccountName(): string {
        return this.bankAccountOptions.find(
            (x) => x.value === this.formGroup.get(FormField.BankAccountKey).value
        ).label;
    }

    public handleAdditionalRequestClick(): void {
        this.template = this.requestTemplate;
        this.resetFormGroup();
    }

    public handleCancelRequestClick(): void {
        this.cancelReserveRelease(this.cancelReserveReleaseRequest);
    }

    public addReserveRelease(): void {
        this.submitting = true;
        if (this.formGroup.valid) {
            this._requestedAmount = this.formGroup.value.requestAmount;
            const request = new AddReserveReleaseRequest({
                bankAccountKey: this.formGroup.value.bankAccountKey,
                requestAmount: this.formGroup.value.requestAmount,
                currentAvailability: this.availability,
                notes: this.formGroup.value.notes
            });
            this.addReserveReleaseRequestStream.next(request);
        }
    }

    public returnToDashboard(): void {
        this._userService.navigateToHome();
    }

    public setRequestAmount(isDefault: boolean): void {
        const control = this.formGroup.get(FormField.RequestAmount);
        const value = control.value;
        if (!this.disabledDefaultAmount) {
            if (isDefault) {
                if (value !== null) {
                    this._savedRequestAmount = value;
                }
                this.formGroup.controls.requestAmount.setValue(this.availableToWithdraw);
            } else {
                this.formGroup.controls.requestAmount.setValue(this._savedRequestAmount);
            }
        }
    }

    public resetFormGroup(): void {
        this.displayNoteInput = false;
        this.formGroup.reset();
        this.formGroup.get(FormField.IsDefault).setValue(!this.disabledDefaultAmount);
        this.formGroup.get(FormField.Notes).setValue(null);
        this.formGroup.get(FormField.RequestAmount).setValue(null);
        if (this.getAvailabilityByClientRequest) {
            this.getAvailabilityByClientRequest = { ...this.getAvailabilityByClientRequest };
            this.getAvailabilityByClient(this.getAvailabilityByClientRequest);
        }
        this.selectDefaultBankAccount();
    }

    public selectDefaultBankAccount(): void {
        this.bankAccountsList.forEach((account) => {
            if (account.isDefault) {
                this.formGroup.get(FormField.BankAccountKey).setValue(account.bankAccountKey);
            }
        });
    }

    public toggleNoteInput(): void {
        this.displayNoteInput = !this.displayNoteInput;
    }
}
